import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarIcon, ChevronLeft, ChevronRight, ClipboardList, Compass, AlertTriangle, Users } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { StudentPicker } from "@/components/StudentPicker";

const KIND_COLORS_IMP = { ministeriale: "#5B7FE2", visita: "#7C3AED", uscita: "#E2725B", permesso: "#F59E0B", altro: "#556B2F" };
const KIND_LABELS = { ministeriale: "Ministeriale", visita: "Visita", uscita: "Uscita", permesso: "Permesso", altro: "Altro" };

const DAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7:00 → 21:00

function toISO(d) {
  // Data locale (non UTC) per evitare offset di fuso che scala il giorno
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fromISO(s) { const [y,m,d] = s.split("-").map(Number); return new Date(y, m-1, d); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d) {
  const x = new Date(d);
  const wd = (x.getDay() + 6) % 7; // Lun=0..Dom=6
  x.setDate(x.getDate() - wd);
  return x;
}
function timeToMin(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}

/** Ordina eventi per studente aggregando tutte le occupazioni. */
function buildStudentTimeline(turni, impegni, dateISO) {
  const map = {};
  turni.filter((t) => t.date === dateISO).forEach((t) => {
    const sid = t.student_id;
    (map[sid] ||= { name: t.student_name || "—", events: [] }).events.push({
      id: `turno-${t.id}`, kind: "turno", title: t.mansione_name || "Turno",
      color: t.mansione_color || "#556B2F", start: t.start_time, end: t.end_time,
      note: t.note, override: t.override_conflict,
    });
  });
  impegni.filter((i) => i.date === dateISO).forEach((imp) => {
    (imp.student_ids || []).forEach((sid, idx) => {
      const sname = imp.student_names?.[idx] || "—";
      (map[sid] ||= { name: sname, events: [] }).events.push({
        id: `imp-${imp.id}-${sid}`, kind: imp.kind, title: imp.title,
        color: KIND_COLORS_IMP[imp.kind] || "#5B7FE2", start: imp.start_time, end: imp.end_time,
        location: imp.location, subtype: imp.kind,
      });
    });
  });
  // ordina per numero eventi decrescente
  return Object.entries(map)
    .map(([sid, v]) => ({ student_id: sid, ...v, events: v.events.sort((a,b) => (timeToMin(a.start)||0) - (timeToMin(b.start)||0)) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default function Planner() {
  const [view, setView] = useState("week"); // day | week
  const [cursor, setCursor] = useState(new Date());
  const [studentFilter, setStudentFilter] = useState("");
  const [show, setShow] = useState("all"); // all | turni | impegni

  const rangeStart = view === "day" ? cursor : startOfWeek(cursor);
  const rangeEnd = view === "day" ? cursor : addDays(rangeStart, 6);
  const since = toISO(rangeStart);
  const until = toISO(rangeEnd);

  const { data: raw = { turni: [], impegni: [] } } = useQuery({
    queryKey: ["planner-calendar", { since, until, studentFilter }],
    queryFn: async () => (await api.get("/mansioni-area/planner/calendar", { params: { since, until, student_id: studentFilter || undefined } })).data,
  });
  const data = useMemo(() => ({
    turni: show === "impegni" ? [] : raw.turni,
    impegni: show === "turni" ? [] : raw.impegni,
  }), [raw, show]);
  const { data: students = [] } = useQuery({
    queryKey: ["students-list", "resid"],
    queryFn: async () => (await api.get("/students", { params: { track_type: "residenziale" } })).data,
  });

  const navigate = (dir) => {
    const step = view === "day" ? 1 : 7;
    setCursor(addDays(cursor, dir * step));
  };

  // Conteggio conflitti
  const conflictSet = useMemo(() => {
    const s = new Set();
    const byDate = {};
    data.turni.forEach((t) => { (byDate[t.date] ||= { turni: [], impegni: [] }).turni.push(t); });
    data.impegni.forEach((e) => { (byDate[e.date] ||= { turni: [], impegni: [] }).impegni.push(e); });
    for (const [d, { turni, impegni }] of Object.entries(byDate)) {
      for (const t of turni) {
        for (const i of impegni) {
          if (!(i.student_ids || []).includes(t.student_id)) continue;
          const overlap = (!t.start_time || !i.start_time) ||
            (t.start_time < (i.end_time || i.start_time) && i.start_time < (t.end_time || t.start_time));
          if (overlap) s.add(`${d}|${t.student_id}`);
        }
      }
    }
    return s;
  }, [data]);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1600px] mx-auto">
      <PageHeader
        title="Planner unificato"
        subtitle="Vista d'insieme di turni e impegni con timeline oraria per studente. Passa da giornaliera a settimanale."
        actions={
          <div className="flex gap-2 flex-wrap">
            <div className="inline-flex rounded-full border border-stone-200 p-0.5 bg-white" data-testid="planner-show">
              {[["all", "Tutto"], ["turni", "Solo turni"], ["impegni", "Solo impegni esterni"]].map(([k, l]) => (
                <button key={k} onClick={() => setShow(k)} data-testid={`planner-show-${k}`} className={`px-3 py-1 text-xs rounded-full transition-colors ${show === k ? "bg-[#E2725B] text-white" : "text-stone-600"}`}>{l}</button>
              ))}
            </div>
            <div className="inline-flex rounded-full border border-stone-200 p-0.5 bg-white">
              <button onClick={() => setView("day")} data-testid="planner-view-day" className={`px-3 py-1 text-xs rounded-full transition ${view === "day" ? "bg-[#1C352D] text-white" : "text-stone-600"}`}>Giorno</button>
              <button onClick={() => setView("week")} data-testid="planner-view-week" className={`px-3 py-1 text-xs rounded-full transition ${view === "week" ? "bg-[#1C352D] text-white" : "text-stone-600"}`}>Settimana</button>
            </div>
          </div>
        }
      />

      {/* Header navigazione */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="planner-prev"><ChevronLeft size={14}/></Button>
          <Button variant="outline" onClick={() => setCursor(new Date())} data-testid="planner-today" className="text-xs">Oggi</Button>
          <Button variant="outline" size="icon" onClick={() => navigate(1)} data-testid="planner-next"><ChevronRight size={14}/></Button>
          <div className="ml-3 text-sm font-medium">
            {view === "day" ? fmtDate(since) : `${fmtDate(since)} — ${fmtDate(until)}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="text-xs text-stone-500 whitespace-nowrap">Filtra studente:</div>
          <div className="flex-1">
            <StudentPicker students={students} value={studentFilter} onChange={setStudentFilter} testId="planner-student-filter" placeholder="Tutti — cerca per filtrare…"/>
          </div>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
        <Card className="p-3 border border-emerald-200 bg-emerald-50/40 rounded-xl shadow-none">
          <div className="flex items-center gap-2 text-emerald-900"><ClipboardList size={13}/> Turni</div>
          <div className="text-xl font-semibold mt-0.5">{data.turni.length}</div>
        </Card>
        <Card className="p-3 border border-sky-200 bg-sky-50/40 rounded-xl shadow-none">
          <div className="flex items-center gap-2 text-sky-900"><Compass size={13}/> Impegni</div>
          <div className="text-xl font-semibold mt-0.5">{data.impegni.length}</div>
        </Card>
        <Card className="p-3 border border-stone-200 rounded-xl shadow-none">
          <div className="flex items-center gap-2 text-stone-700"><Users size={13}/> Studenti impegnati</div>
          <div className="text-xl font-semibold mt-0.5">
            {new Set([...data.turni.map((t) => t.student_id), ...data.impegni.flatMap((i) => i.student_ids || [])]).size}
          </div>
        </Card>
        <Card className={`p-3 border rounded-xl shadow-none ${conflictSet.size > 0 ? "border-red-300 bg-red-50/40" : "border-stone-200"}`}>
          <div className="flex items-center gap-2"><AlertTriangle size={13} className={conflictSet.size > 0 ? "text-red-700" : "text-stone-400"}/> Conflitti</div>
          <div className={`text-xl font-semibold mt-0.5 ${conflictSet.size > 0 ? "text-red-700" : ""}`}>{conflictSet.size}</div>
        </Card>
      </div>

      {view === "day"
        ? <DayView dateISO={since} turni={data.turni} impegni={data.impegni} conflictSet={conflictSet}/>
        : <WeekView start={rangeStart} turni={data.turni} impegni={data.impegni} conflictSet={conflictSet}/>}
    </div>
  );
}

// ============ DAY VIEW ============
function DayView({ dateISO, turni, impegni, conflictSet }) {
  const timeline = buildStudentTimeline(turni, impegni, dateISO);

  if (timeline.length === 0) {
    return <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">Nessuna attività assegnata per questo giorno.</Card>;
  }

  return (
    <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
      <div className="px-4 py-2 bg-stone-50 border-b border-stone-100 text-sm font-medium flex items-center gap-2">
        <CalendarIcon size={14}/> Programma di {fmtDate(dateISO)} · {timeline.length} studenti impegnati
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Header ore */}
          <div className="grid" style={{ gridTemplateColumns: "180px repeat(15, minmax(60px, 1fr))" }}>
            <div className="p-2 text-[10px] uppercase tracking-widest text-stone-400 border-b border-stone-100">Studente</div>
            {HOURS.map((h) => (
              <div key={h} className="p-2 text-[10px] text-stone-400 border-b border-l border-stone-100 text-center">{h}:00</div>
            ))}
          </div>
          {/* Righe studenti */}
          {timeline.map((row) => {
            const hasConflict = conflictSet.has(`${dateISO}|${row.student_id}`);
            return (
              <div key={row.student_id} className={`grid border-b border-stone-100 ${hasConflict ? "bg-red-50/40" : ""}`} style={{ gridTemplateColumns: "180px repeat(15, minmax(60px, 1fr))" }} data-testid={`day-row-${row.student_id}`}>
                <div className="p-2 text-sm border-r border-stone-100 flex items-center gap-1.5 truncate">
                  {hasConflict && <AlertTriangle size={12} className="text-red-600 flex-shrink-0"/>}
                  <span className="truncate">{row.name}</span>
                </div>
                <div className="col-span-15 relative min-h-[42px]" style={{ gridColumn: "2 / -1" }}>
                  {/* Griglia ore di sfondo */}
                  <div className="absolute inset-0 grid pointer-events-none" style={{ gridTemplateColumns: "repeat(15, 1fr)" }}>
                    {HOURS.map((h) => <div key={h} className="border-l border-stone-100"/>)}
                  </div>
                  {/* Eventi */}
                  {row.events.map((ev) => {
                    const startMin = timeToMin(ev.start);
                    const endMin = timeToMin(ev.end);
                    const rangeStart = 7 * 60;
                    const rangeEnd = 22 * 60;
                    let leftPct, widthPct;
                    if (startMin == null) {
                      leftPct = 0; widthPct = 100; // all day
                    } else {
                      const s = Math.max(rangeStart, startMin);
                      const e = endMin != null ? Math.min(rangeEnd, endMin) : Math.min(rangeEnd, s + 60);
                      leftPct = ((s - rangeStart) / (rangeEnd - rangeStart)) * 100;
                      widthPct = Math.max(6, ((e - s) / (rangeEnd - rangeStart)) * 100);
                    }
                    return (
                      <div
                        key={ev.id}
                        title={`${ev.title}${ev.start ? ` · ${ev.start}${ev.end ? "–"+ev.end : ""}` : " · tutto il giorno"}${ev.location ? ` · ${ev.location}` : ""}`}
                        className="absolute top-1 bottom-1 rounded px-1.5 py-0.5 text-[10px] text-white truncate flex items-center shadow-sm"
                        style={{ left: `${leftPct}%`, width: `${widthPct}%`, background: ev.color, zIndex: 2 }}
                        data-testid={`day-evt-${ev.id}`}
                      >
                        <span className="truncate">
                          <b>{ev.title}</b>
                          {ev.start && <span className="opacity-90"> {ev.start}{ev.end ? "–"+ev.end : ""}</span>}
                          {ev.location && <span className="opacity-80"> · {ev.location}</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

// ============ WEEK VIEW ============
function WeekView({ start, turni, impegni, conflictSet }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));

  // Raggruppa per giorno → studente → eventi
  const byDay = days.map((d) => ({
    date: d,
    iso: toISO(d),
    students: buildStudentTimeline(turni, impegni, toISO(d)),
  }));
  const totalCount = byDay.reduce((a, b) => a + b.students.length, 0);

  if (totalCount === 0) {
    return <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">Nessuna attività assegnata questa settimana.</Card>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-7 gap-3">
      {byDay.map((d, i) => (
        <Card key={d.iso} className={`border rounded-xl shadow-none overflow-hidden ${d.students.length === 0 ? "border-stone-200 bg-stone-50/30" : "border-stone-200"}`}>
          <div className={`px-3 py-2 border-b text-xs font-medium flex items-center justify-between ${
            d.date.toDateString() === new Date().toDateString() ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-stone-50 border-stone-100"
          }`}>
            <span>{DAY_NAMES[i]} {d.date.getDate()}</span>
            {d.students.length > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              d.date.toDateString() === new Date().toDateString() ? "bg-white/20" : "bg-white border border-stone-200 text-stone-600"
            }`}>{d.students.length}</span>}
          </div>
          <div className="p-2 space-y-1.5 min-h-[100px] max-h-[500px] overflow-y-auto">
            {d.students.length === 0 && <div className="text-[10px] text-stone-300 italic px-1 pt-1">—</div>}
            {d.students.map((row) => {
              const hasConflict = conflictSet.has(`${d.iso}|${row.student_id}`);
              return (
                <div key={row.student_id} className={`rounded p-1.5 text-[11px] ${hasConflict ? "bg-red-50 border border-red-200" : "bg-stone-50 border border-stone-100"}`} data-testid={`week-row-${d.iso}-${row.student_id}`}>
                  <div className="flex items-center gap-1 font-medium text-stone-800 truncate">
                    {hasConflict && <AlertTriangle size={10} className="text-red-600 flex-shrink-0"/>}
                    <span className="truncate">{row.name}</span>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {row.events.map((ev) => (
                      <div key={ev.id} className="flex items-center gap-1 truncate" title={ev.title}>
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ev.color }}/>
                        <span className="truncate">
                          {ev.start && <span className="text-stone-500">{ev.start} </span>}
                          <b className="text-stone-800">{ev.title}</b>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}
